import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowUpRight,
  BriefcaseBusiness,
  CheckCircle2,
  ClipboardCheck,
  Gauge,
  ShoppingBag,
  UsersRound,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { DataPanel } from '../../components/DataStates';
import { StorePicker } from '../../components/StorePicker';
import { UserAvatar } from '../../components/UserAvatar';
import { apiRequest } from '../../lib/api';
import type { BrandAmbassadorPerformance, PerformancePage as PerformanceResponse } from '../../types/domain';

type DateRangePreset = '7_days' | '30_days' | '90_days' | '180_days' | '365_days' | 'custom';

interface PerformanceFilters {
  client: string;
  agency: string;
  campaign: string;
  venue: string;
  brandAmbassador: string;
  dateRange: DateRangePreset;
  dateFrom: string;
  dateTo: string;
  ordering: string;
}

const dateRangeDays: Record<Exclude<DateRangePreset, 'custom'>, number> = {
  '7_days': 7,
  '30_days': 30,
  '90_days': 90,
  '180_days': 180,
  '365_days': 365,
};

const currencyFormatter = new Intl.NumberFormat('en-PK', {
  style: 'currency',
  currency: 'PKR',
  maximumFractionDigits: 2,
});

function targetProgressLabel(row: BrandAmbassadorPerformance) {
  if (!row.target_progress.length) return 'No target';
  return row.target_progress.map((target) => {
    const achieved = Number(target.achieved_value).toLocaleString('en-PK');
    const goal = Number(target.target_value).toLocaleString('en-PK');
    return target.display_unit === 'PKR'
      ? `PKR ${achieved} / PKR ${goal}`
      : `${achieved} / ${goal} ${target.display_unit}`;
  }).join(' · ');
}

function pakistanDateInput(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Asia/Karachi',
  }).formatToParts(date);
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}

function dateInputDaysBefore(value: string, days: number) {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}

function initialFilters(): PerformanceFilters {
  const today = pakistanDateInput();
  return {
    client: '',
    agency: '',
    campaign: '',
    venue: '',
    brandAmbassador: '',
    dateRange: '30_days',
    dateFrom: dateInputDaysBefore(today, 30),
    dateTo: today,
    ordering: '-target_attainment',
  };
}

function PercentageBar({ label, value }: { label: string; value: string | null }) {
  const percentage = value == null ? null : Number(value);
  const width = Math.max(0, Math.min(100, percentage ?? 0));
  return <div className="performance-progress">
    <span><strong>{label}</strong><b>{percentage == null ? 'No target' : `${percentage.toFixed(1)}%`}</b></span>
    <div aria-label={`${label}: ${percentage == null ? 'No target' : `${percentage.toFixed(1)}%`}`} aria-valuemax={100} aria-valuemin={0} aria-valuenow={percentage ?? undefined} className="progress-track" role="progressbar"><span style={{ width: `${width}%` }} /></div>
  </div>;
}

function Scorecard({ row, recordsPath }: {
  row: BrandAmbassadorPerformance;
  recordsPath: (resource: 'attendance' | 'sales', row: BrandAmbassadorPerformance) => string;
}) {
  return <article aria-labelledby={`performance-${row.id}`} className="content-card performance-card">
    <div className="performance-card-overview">
      <UserAvatar className="avatar large" name={row.name} src={row.avatar} />
      <div className="performance-card-heading">
        <div><h2 id={`performance-${row.id}`}>{row.name}</h2><p>{row.campaign_count} {row.campaign_count === 1 ? 'campaign' : 'campaigns'} in scope</p></div>
        <span className="performance-shift-count"><strong>{row.attended_shifts}/{row.scheduled_shifts}</strong><small>shifts attended</small></span>
      </div>

      <div className="performance-bars">
        <PercentageBar label="Attendance compliance" value={row.attendance_compliance_percentage} />
        <PercentageBar label="Punctuality" value={row.punctuality_percentage} />
        <PercentageBar label="Store target contribution" value={row.target_attainment_percentage} />
      </div>
    </div>

    <div className="performance-details">
      <div><span><BriefcaseBusiness size={16} /> Assignment response</span><strong>{row.assignment_acceptance_percentage}%</strong><small>{row.assignment_accepted + row.assignment_completed} accepted · {row.assignment_declined} declined · completion {row.assignment_completion_percentage}%</small></div>
      <div><span><CheckCircle2 size={16} /> Approved check-ins</span><strong>{row.approved_check_ins}</strong><small>{row.on_time_check_ins} on time · {row.late_check_ins} late</small></div>
      <div><span><ShoppingBag size={16} /> Approved sales</span><strong>{row.approved_sales_units} units</strong><small>{row.approved_sales_value == null ? 'Value unavailable' : currencyFormatter.format(Number(row.approved_sales_value))}</small></div>
      <div><span><Gauge size={16} /> Target contribution</span><strong>{targetProgressLabel(row)}</strong><small>Approved sales against metric-based campaign targets</small></div>
    </div>

    <div className="performance-exceptions">
      <span className={row.flagged_events ? 'warning' : ''}><AlertTriangle size={15} /> {row.flagged_events} flagged</span>
      <span className={row.rejected_events ? 'danger' : ''}>{row.rejected_events} rejected</span>
    </div>
    <div className="performance-actions">
      <Link className="text-button" to={recordsPath('attendance', row)}>Attendance records <ArrowUpRight size={14} /></Link>
      <Link className="text-button" to={recordsPath('sales', row)}>Sales records <ArrowUpRight size={14} /></Link>
    </div>
  </article>;
}

export function PerformancePage() {
  const { accessToken, selectedWorkspace } = useAuth();
  const workspaceId = selectedWorkspace?.id ?? null;
  const [filterState, setFilterState] = useState<{
    workspaceId: string | null;
    filters: PerformanceFilters;
  }>(() => ({ workspaceId, filters: initialFilters() }));
  const [page, setPage] = useState(1);
  const filters = filterState.workspaceId === workspaceId ? filterState.filters : initialFilters();
  const params = new URLSearchParams({
    page: String(page),
    page_size: '12',
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
    ordering: filters.ordering,
  });
  if (selectedWorkspace?.organizationId) params.set('workspace', selectedWorkspace.organizationId);
  if (filters.client) params.set('client', filters.client);
  if (filters.agency) params.set('agency', filters.agency);
  if (filters.campaign) params.set('campaign', filters.campaign);
  if (filters.venue) params.set('venue', filters.venue);
  if (filters.brandAmbassador) params.set('brand_ambassador', filters.brandAmbassador);
  const performancePath = `/performance/brand-ambassadors/?${params.toString()}`;
  const performance = useQuery({
    queryKey: ['ba-performance', performancePath, accessToken],
    enabled: Boolean(accessToken),
    placeholderData: (previousData) => previousData,
    queryFn: () => apiRequest<PerformanceResponse>(
      performancePath,
      accessToken ? { accessToken } : {},
    ),
  });
  const data = performance.data;
  const rows = data?.results ?? [];

  function updateFilter<Key extends keyof PerformanceFilters>(
    key: Key,
    value: PerformanceFilters[Key],
  ) {
    setFilterState((current) => ({
      workspaceId,
      filters: {
        ...(current.workspaceId === workspaceId ? current.filters : initialFilters()),
        [key]: value,
      },
    }));
    setPage(1);
  }

  function updateDateRange(dateRange: DateRangePreset) {
    const dateTo = filters.dateTo || pakistanDateInput();
    setFilterState((current) => {
      const currentFilters = current.workspaceId === workspaceId ? current.filters : initialFilters();
      return {
        workspaceId,
        filters: {
          ...currentFilters,
          dateRange,
          dateTo,
          dateFrom: dateRange === 'custom'
            ? currentFilters.dateFrom
            : dateInputDaysBefore(dateTo, dateRangeDays[dateRange]),
        },
      };
    });
    setPage(1);
  }

  function updateCustomDate(key: 'dateFrom' | 'dateTo', value: string) {
    setFilterState((current) => ({
      workspaceId,
      filters: {
        ...(current.workspaceId === workspaceId ? current.filters : initialFilters()),
        dateRange: 'custom',
        [key]: value,
      },
    }));
    setPage(1);
  }

  function resetFilters() {
    setFilterState({ workspaceId, filters: initialFilters() });
    setPage(1);
  }

  function recordsPath(resource: 'attendance' | 'sales', row: BrandAmbassadorPerformance) {
    const recordParams = new URLSearchParams({
      brand_ambassador: row.id,
      date_from: filters.dateFrom,
      date_to: filters.dateTo,
    });
    if (filters.client) recordParams.set('client', filters.client);
    if (filters.agency) recordParams.set('agency', filters.agency);
    if (filters.campaign) recordParams.set('campaign', filters.campaign);
    if (filters.venue) recordParams.set('venue', filters.venue);
    return `/${resource}?${recordParams.toString()}`;
  }

  const summary = data?.summary;
  return <>
    <header className="page-heading">
      <div><p className="eyebrow">{selectedWorkspace?.name}</p><h1>BA performance</h1><p>Explainable attendance, assignment, and approved-sales measures from authorised operational records.</p></div>
      <span className="record-count"><UsersRound size={16} /> {data?.count ?? 0} ambassadors</span>
    </header>

    <section className="content-card attendance-filter-card performance-filter-card">
      <div className="card-heading"><div><h2>Filter performance</h2><p>Every scorecard uses the same workspace, campaign context, and reporting period.</p></div><button className="text-button" onClick={resetFilters} type="button">Reset filters</button></div>
      <div className="attendance-filter-grid performance-filter-grid">
        <label>Client<select aria-label="Performance client" onChange={(event) => updateFilter('client', event.target.value)} value={filters.client}><option value="">All clients</option>{(data?.filters.clients ?? []).map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}</select></label>
        <label>Agency<select aria-label="Performance agency" onChange={(event) => updateFilter('agency', event.target.value)} value={filters.agency}><option value="">All agencies</option>{(data?.filters.agencies ?? []).map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}</select></label>
        <label>Campaign<select aria-label="Performance campaign" onChange={(event) => updateFilter('campaign', event.target.value)} value={filters.campaign}><option value="">All campaigns</option>{(data?.filters.campaigns ?? []).map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}</select></label>
        <label>Store<StorePicker allOptionLabel="All stores" ariaLabel="Performance store" onChange={(value) => updateFilter('venue', value)} options={(data?.filters.venues ?? []).map((option) => ({ id: option.id, name: option.name }))} value={filters.venue} /></label>
        <label>Brand ambassador<select aria-label="Performance brand ambassador" onChange={(event) => updateFilter('brandAmbassador', event.target.value)} value={filters.brandAmbassador}><option value="">All ambassadors</option>{(data?.filters.brand_ambassadors ?? []).map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}</select></label>
        <label>Sort by<select aria-label="Performance ordering" onChange={(event) => updateFilter('ordering', event.target.value)} value={filters.ordering}><option value="name">Name A–Z</option><option value="-name">Name Z–A</option><option value="-attendance_compliance">Attendance compliance</option><option value="-punctuality">Punctuality</option><option value="-approved_sales_units">Approved sales</option><option value="-target_attainment">Target contribution</option></select></label>
        <label>Date range<select aria-label="Performance date range" onChange={(event) => updateDateRange(event.target.value as DateRangePreset)} value={filters.dateRange}><option value="7_days">1 week</option><option value="30_days">1 month</option><option value="90_days">3 months</option><option value="180_days">6 months</option><option value="365_days">1 year</option><option value="custom">Custom</option></select></label>
        <label>Date from<input max={filters.dateTo} onChange={(event) => updateCustomDate('dateFrom', event.target.value)} type="date" value={filters.dateFrom} /></label>
        <label>Date to<input min={filters.dateFrom} onChange={(event) => updateCustomDate('dateTo', event.target.value)} type="date" value={filters.dateTo} /></label>
      </div>
    </section>

    {summary ? <section aria-label="Performance summary" className="metric-grid performance-summary">
      <article className="metric-card"><span className="icon-chip"><UsersRound size={18} /></span><p>Ambassadors</p><strong>{summary.ambassadors}</strong><small>With assignment activity in scope</small></article>
      <article className="metric-card"><span className="icon-chip"><ClipboardCheck size={18} /></span><p>Attended shifts</p><strong>{summary.attended_shifts}/{summary.scheduled_shifts}</strong><small>Approved check-in evidence</small></article>
      <article className="metric-card"><span className="icon-chip"><ShoppingBag size={18} /></span><p>Approved sales</p><strong>{summary.approved_sales_units}</strong><small>{summary.approved_sales_value == null ? 'Value unavailable' : currencyFormatter.format(Number(summary.approved_sales_value))}</small></article>
      <article className="metric-card"><span className="icon-chip attendance-warning"><AlertTriangle size={18} /></span><p>Exceptions</p><strong>{summary.flagged_events + summary.rejected_events}</strong><small>{summary.flagged_events} flagged · {summary.rejected_events} rejected</small></article>
    </section> : null}

    <p className="capability-note performance-method-note"><strong>No composite ranking:</strong> target contribution compares approved targeted sales with the configured targets of stores assigned to that BA; a shared store target is not an individual quota.</p>

    <DataPanel loading={performance.isLoading} error={performance.isError} empty={!performance.isLoading && rows.length === 0}>
      <section aria-label="Brand ambassador scorecards" className="performance-grid">
        {rows.map((row) => <Scorecard key={row.id} recordsPath={recordsPath} row={row} />)}
      </section>
    </DataPanel>

    {data && data.count > 0 ? <div className="attendance-pagination"><span>Page {page} · {data.count} matching ambassadors</span><div><button className="button secondary" disabled={!data.previous} onClick={() => setPage((value) => Math.max(1, value - 1))} type="button">Previous</button><button className="button secondary" disabled={!data.next} onClick={() => setPage((value) => value + 1)} type="button">Next</button></div></div> : null}
  </>;
}
